import { cn } from "@/modules/lib/utils"
import { InformationAlertIcon } from "@images/svgIcons/informationAlertIcon"
import { getRecurringPeriodShortLabel } from "@modules/boxOffer/utils/boxOfferUtils"
import { PriceObjectEntity } from "@modules/common/priceObject/entities/priceObjectEntity"
import { word } from "@modules/core/utils/i18n"
import { currencySymbol, formatCurrency, getCurrencySeparator } from "@modules/currency/currency"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { Text, TextVariants } from "@/components/ui/baseUi/text"
import styled from "styled-components"
import type { VariantProps } from "class-variance-authority"

type BoxOfferFormattedPriceProps = {
  price: PriceObjectEntity | null
  showRecurringPeriod: boolean
  recurringPeriod?: string | null
  wholePartStyle?: VariantProps<typeof TextVariants>["fontStyle"]
  decimalPartStyle?: VariantProps<typeof TextVariants>["fontStyle"]
  periodStyle?: VariantProps<typeof TextVariants>["fontStyle"]
  lineThrough?: boolean
  classNameHutext?: string
  className?: string
} & React.ComponentProps<"span">

type BoxOfferFormSectionProps = {
  title: string
  icon: React.ReactNode
  description?: string
  className?: string
}

type BoxOfferInputCustomLabelProps = {
  label: string
  description?: string
  isRequired?: boolean
  margin?: string
}

type BoxOfferFormAlertCardProps = {
  title: string
  description?: string
}

export const BoxOfferFormattedPrice = ({
  price,
  showRecurringPeriod,
  recurringPeriod,
  wholePartStyle = "HS",
  decimalPartStyle = "TS",
  periodStyle = "BS",
  lineThrough,
  classNameHutext,
  className,
  ...props
}: BoxOfferFormattedPriceProps) => {
  const language = useLanguage()

  if (!price)
    return (
      <Text fontStyle={wholePartStyle} className={classNameHutext} {...props}>
        {word("offer_list.price.free")}
      </Text>
    )
  const symbol = currencySymbol(price.currency, language)
  const currencySeparator = getCurrencySeparator(language)

  const formattedPrice = formatCurrency(price.value, price.currency, language).replace(symbol, "")
  const [wholePart, decimalPart] = formattedPrice.split(currencySeparator)

  return (
    <span className={cn("whitespace-nowrap", className)}>
      <Text fontStyle={wholePartStyle} className={cn(classNameHutext, lineThrough && "text-textDisabled line-through")}>
        {wholePart}
      </Text>
      <Text
        fontStyle={decimalPartStyle}
        className={cn(classNameHutext, lineThrough && "text-textDisabled line-through")}
      >
        {decimalPart ? `${currencySeparator}${decimalPart.trim()}${symbol}` : `${symbol}`}
      </Text>
      {showRecurringPeriod && (
        <Text fontStyle={periodStyle} className={cn(classNameHutext, lineThrough && "text-textDisabled line-through")}>
          {` ${getRecurringPeriodShortLabel(recurringPeriod)}`}
        </Text>
      )}
    </span>
  )
}

export const BoxOfferFormSection = ({ title, icon, description, className }: BoxOfferFormSectionProps) => {
  return (
    <div className={className ?? "mb-2"}>
      <div className="flex items-center gap-2">
        {icon}
        <Text fontStyle="TM">{title}</Text>
      </div>
      {description && (
        <Text fontStyle="BS" className="mt-2 flex text-neutral-700">
          {description}
        </Text>
      )}
    </div>
  )
}

export const BoxOfferInputCustomLabel = ({
  label,
  description,
  margin,
  isRequired = false,
}: BoxOfferInputCustomLabelProps) => {
  return (
    <div className={`flex flex-col gap-2 ${margin ?? "mb-3"}`}>
      <Text fontStyle="LM" className="text-neutral-700">
        {label.toUpperCase()}
        {isRequired && (
          <Text fontStyle="LM" className="text-red-500">
            {" "}
            *
          </Text>
        )}
      </Text>
      {description && (
        <Text fontStyle="BS" className="text-neutral-700">
          {description}
        </Text>
      )}
    </div>
  )
}

export const BoxOfferRuleCard = styled.div<{
  $new?: boolean
  $borderSize?: number
  $pointer?: boolean
  $resume?: boolean
}>`
  background-color: var(--neutral-50);
  cursor: ${({ $pointer }) => $pointer && "pointer"};
  display: flex;
  flex-direction: ${({ $new }) => ($new ? "column" : "row")};
  padding: 1.75rem 2rem;
  align-items: center;
  border-radius: 1rem;
  gap: 0.6rem;
  border: ${({ $new, $borderSize }) =>
    `${$borderSize ?? 1}px ${$new ? `dashed var(--neutral-400)` : `solid var(--primary-500)`}`};
  max-width: ${({ $new, $resume }) => ($resume ? "100%" : $new ? "fit-content" : "12rem")};
  height: 6.75rem;
`

export function BoxOfferFormAlertCard({
  title,
  description,
  className,
  ...props
}: BoxOfferFormAlertCardProps & React.ComponentProps<"div">) {
  return (
    <div className={cn("card banner flex flex-row items-center gap-4 rounded-2xl px-4 py-2", className)} {...props}>
      <div>
        <InformationAlertIcon backgroundColor={"var(--primary-900)"} color={"var(--primary-300)"} />
      </div>
      <div className="flex flex-col">
        <Text fontStyle="BL">{title}</Text>
        {description && <Text fontStyle="BM">{description}</Text>}
      </div>
    </div>
  )
}
